import React, { useCallback, useRef, useLayoutEffect } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';
import styled from 'styled-components';

type Props<Item extends { id: string }> = {
  fetchingNextPage: boolean;
  fetchingPreviousPage: boolean;
  onFetchNextPage?: () => void;
  onFetchPreviousPage?: () => void;
  items: Item[];
  itemSize: number;
  children: ({ item, style }: { item: Item; style: React.CSSProperties }) => JSX.Element;
  fetchThreshold?: number;
};

export const countItemsPrepended = (previousItems: { id: string }[], nextItems: { id: string }[]) => {
  if (previousItems.length === 0) {
    return nextItems.length;
  }

  const previousFirstItemId = previousItems[0].id;
  const itemIndex = nextItems.findIndex(item => item.id === previousFirstItemId);

  // If previous item not found from the list, then we cannot know how many were prepended
  if (itemIndex === -1) {
    return undefined;
  }

  return itemIndex;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const InfiniteList = <Item extends { id: string }>({
  fetchingNextPage,
  fetchingPreviousPage,
  onFetchNextPage,
  onFetchPreviousPage,
  items,
  itemSize,
  children,
  fetchThreshold = 2000, // When user gets closer than this to the start or end of the list, fetch more items
}: Props<Item>) => {
  const listRef = useRef<List>(null);
  const scrollOffset = useRef<number | null>(null);
  const viewportHeight = useRef<number | null>(null);
  const previousItems = useRef(items);

  const handleListResize = useCallback(({ height }: { height: number }) => {
    viewportHeight.current = height;
  }, []);

  const handleListScroll = useCallback(
    (event: { scrollOffset: number }) => {
      scrollOffset.current = event.scrollOffset;

      // Only call either onFetchPreviousPage or onFetchNextPage but not both, because
      // react-query seems to have a bug that causes fetchPreviousPage results to get
      // discarded if fetchNextPage is called right after it.
      if (onFetchPreviousPage && !fetchingPreviousPage && scrollOffset.current < fetchThreshold) {
        onFetchPreviousPage();
      } else if (
        viewportHeight.current !== null &&
        onFetchNextPage &&
        !fetchingNextPage &&
        scrollOffset.current + viewportHeight.current >= itemSize * items.length - fetchThreshold
      ) {
        onFetchNextPage();
      }
    },
    [onFetchPreviousPage, fetchingPreviousPage, onFetchNextPage, fetchingNextPage, items.length], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useLayoutEffect(() => {
    // When items are prepended to the list, adjust the scroll position so that
    // the items that the user is looking at stay still and don't jump.
    const numItemsPrepended = countItemsPrepended(previousItems.current, items);
    const itemsOverflow = listRef.current && Number(listRef.current.props.height) < itemSize * items.length;
    if (listRef.current && scrollOffset.current !== null && numItemsPrepended && itemsOverflow) {
      listRef.current.scrollTo(scrollOffset.current + numItemsPrepended * itemSize);
    }
    previousItems.current = items;
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Root>
      <AutoSizer onResize={handleListResize}>
        {({ width, height }) => (
          <List
            height={height}
            itemCount={items.length}
            itemSize={itemSize}
            onScroll={handleListScroll}
            ref={listRef}
            width={width}
          >
            {({ index, style }) => {
              const item = items[index];
              return children({ item, style: style as React.CSSProperties });
            }}
          </List>
        )}
      </AutoSizer>
    </Root>
  );
};

export default InfiniteList;
